import { useEffect } from 'react';
import { createSelector } from 'reselect';
import { useRegisterPersistedStore } from '@/stores/PersistenceProvider';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { BASE_TOOL_COMMON_PROPS } from '@/tools/tool.constants';
import { buildToolRehydrationState, getToolConfigParams, sanitizeToolItem } from '@/tools/tool.utilities';
import { type ActionsFactory, type ResettableStoreState, createStore, createStoreInstance } from '@seeqdev/utils';
import type { FrontendDuration } from '@/services/systemConfiguration.types';
import type {
  BaseToolPersistedState,
  DurationWithUnits,
  ParameterDefinitions,
  ToolConfigParams,
  ToolConfigValue,
  ToolItem,
  ToolRehydratePayload,
} from '@/tools/tool.types';

export interface ReferencePanelInputGroup {
  inputSignal: ToolItem;
  inputCondition: ToolItem;
  index?: number;
}

export interface ReferencePanelState extends BaseToolPersistedState {
  repeatOverCondition?: ToolItem;
  windowStart: number;
  windowEnd: number;
  griddingPeriod: FrontendDuration;
  referenceStat: string;
  multiplier: number;
  inputGroups: ReferencePanelInputGroup[];
}

export type ReferencePanelDehydratedState = ReferencePanelState;

export interface ReferencePanelActions {
  setSearchName: (name: string) => void;
  setMaximumDuration: (value: number, units: string) => void;
  setRepeatOverCondition: (condition: ToolItem | string | null | undefined) => void;
  setInputCondition: (condition: ToolItem | null | undefined, index: number) => void;
  setInputSignal: (id: string, index: number) => void;
  addSignalCondition: () => void;
  removeSignalCondition: (index: number) => void;
  setTrainingWindow: (window: { startTime: number; endTime: number }) => void;
  setGriddingPeriod: (griddingPeriod: FrontendDuration) => void;
  setReferenceStat: (referenceStat: string) => void;
  setMultiplier: (multiplier: number) => void;
  rehydrateForEdit: (payload: ToolRehydratePayload) => void;
  reset: ResettableStoreState<ReferencePanelState>;
}

const parameterDefinitions: ParameterDefinitions<'repeatOverCondition'> = {
  repeatOverCondition: { predicate: ['name', 'repeatOverCondition'] },
};

const createInputGroup = (): ReferencePanelInputGroup => ({ inputSignal: {}, inputCondition: {} });

const createInitialState = (): ReferencePanelState => ({
  ...BASE_TOOL_COMMON_PROPS,
  repeatOverCondition: undefined,
  windowStart: 0,
  windowEnd: 0,
  griddingPeriod: { value: 5, units: 'min' },
  referenceStat: '',
  multiplier: 1,
  inputGroups: [createInputGroup()],
});

const initialState = Object.freeze(createInitialState());

export const dehydrateReferencePanelState = (state: ReferencePanelState): ReferencePanelDehydratedState => state;

function getInputGroups(parameters: ToolRehydratePayload['parameters']): ReferencePanelInputGroup[] | undefined {
  if (!parameters) {
    return undefined;
  }

  const inputGroups = new Map<number, ReferencePanelInputGroup>();
  const inputGroupPattern = /^(inputSignal|inputCondition)(\d*)$/;

  parameters.forEach((parameter) => {
    const match = parameter.name?.match(inputGroupPattern);
    if (!match) {
      return;
    }

    const [, parameterName, suffix] = match;
    const number = suffix ? Number(suffix) : 1;
    const group = inputGroups.get(number) ?? createInputGroup();
    group[parameterName as 'inputSignal' | 'inputCondition'] = { id: parameter.item?.id };
    inputGroups.set(number, group);
  });

  return [...inputGroups.entries()].sort(([a], [b]) => a - b).map(([, group]) => group);
}

const referencePanelConfigParamsSelector = createSelector(
  [(state: ReferencePanelState) => state],
  (state): ToolConfigParams => {
    const config = getToolConfigParams({
      serializedState: state as unknown as Record<string, ToolConfigValue>,
      toolType: TREND_TOOLS.REFERENCE,
      parameterDefinitionKeys: Object.keys(parameterDefinitions).concat('inputGroups'),
      advancedParametersCollapsed: state.advancedParametersCollapsed,
    });

    // Reference Profile never persisted the collapsed state in UIConfig.
    delete (config as Partial<ToolConfigParams>).advancedParametersCollapsed;
    return config;
  },
);

const referencePanelActionsFactory: ActionsFactory<ReferencePanelState, ReferencePanelActions> = (
  set,
  _get,
  resetState,
) => ({
  setSearchName: (name) =>
    set((draft) => {
      draft.name = name;
    }),

  setMaximumDuration: (value, units) =>
    set((draft) => {
      draft.maximumDuration = { value, units } as DurationWithUnits;
    }),

  setRepeatOverCondition: (condition) =>
    set((draft) => {
      draft.repeatOverCondition =
        typeof condition === 'string' ? { id: condition } : (sanitizeToolItem(condition) ?? undefined);
    }),

  setInputCondition: (condition, index) =>
    set((draft) => {
      if (index >= 0 && draft.inputGroups[index]) {
        draft.inputGroups[index].inputCondition = condition
          ? { id: condition.id, conditionMetadata: condition.conditionMetadata }
          : {};
      }
    }),

  setInputSignal: (id, index) =>
    set((draft) => {
      if (index >= 0 && draft.inputGroups[index]) {
        draft.inputGroups[index].inputSignal = { id };
      }
    }),

  addSignalCondition: () =>
    set((draft) => {
      draft.inputGroups.push(createInputGroup());
    }),

  removeSignalCondition: (index) =>
    set((draft) => {
      if (index >= 0) {
        draft.inputGroups.splice(index, 1);
      }
    }),

  setTrainingWindow: (window) =>
    set((draft) => {
      draft.windowStart = window.startTime;
      draft.windowEnd = window.endTime;
    }),

  setGriddingPeriod: (griddingPeriod) =>
    set((draft) => {
      draft.griddingPeriod = griddingPeriod;
    }),

  setReferenceStat: (referenceStat) =>
    set((draft) => {
      draft.referenceStat = referenceStat;
    }),

  setMultiplier: (multiplier) =>
    set((draft) => {
      draft.multiplier = multiplier;
    }),

  rehydrateForEdit: (payload) =>
    set((draft) => {
      const rehydrationState = buildToolRehydrationState({
        payload,
        parameterDefinitions,
        currentAdvancedParametersCollapsed: draft.advancedParametersCollapsed,
      });
      const inputGroups = getInputGroups(payload.parameters);

      Object.assign(draft, createInitialState(), rehydrationState.config, rehydrationState.selectedParameters, {
        originalParameters: rehydrationState.originalParameters,
        ...(inputGroups ? { inputGroups } : {}),
      });
    }),

  reset: resetState,
});

const STORE_NAME = 'ReferencePanel';

const [ReferencePanelStoreProvider, useReferencePanelStoreInstance, useReferencePanelStoreSelector] = createStore(
  STORE_NAME,
  initialState,
  referencePanelActionsFactory,
);

export { ReferencePanelStoreProvider };

export const useReferencePanelId = () => useReferencePanelStoreSelector((state) => state.id);
export const useReferencePanelName = () => useReferencePanelStoreSelector((state) => state.name);
export const useReferencePanelOriginalParameters = () =>
  useReferencePanelStoreSelector((state) => state.originalParameters);
export const useReferencePanelInputGroups = () => useReferencePanelStoreSelector((state) => state.inputGroups);
export const useReferencePanelRepeatOverCondition = () =>
  useReferencePanelStoreSelector((state) => state.repeatOverCondition);
export const useReferencePanelGriddingPeriod = () => useReferencePanelStoreSelector((state) => state.griddingPeriod);
export const useReferencePanelReferenceStat = () => useReferencePanelStoreSelector((state) => state.referenceStat);
export const useReferencePanelMultiplier = () => useReferencePanelStoreSelector((state) => state.multiplier);
export const useReferencePanelMaximumDuration = () => useReferencePanelStoreSelector((state) => state.maximumDuration);

const selectTrainingWindow = createSelector(
  (state: ReferencePanelState) => state.windowStart,
  (state: ReferencePanelState) => state.windowEnd,
  (startTime, endTime) => ({ startTime, endTime }),
);

export const useReferencePanelTrainingWindow = () => useReferencePanelStoreSelector(selectTrainingWindow);
export const useReferencePanelConfigParams = () => useReferencePanelStoreSelector(referencePanelConfigParamsSelector);
export const useReferencePanelActions = () => useReferencePanelStoreInstance().actions;

export function createTestReferencePanelStore(overrides?: Partial<ReferencePanelState>) {
  if (process.env.NODE_ENV !== 'test') {
    throw new Error('createTestReferencePanelStore() should only be used in tests.');
  }

  return createStoreInstance({ ...createInitialState(), ...overrides }, referencePanelActionsFactory);
}

/** @deprecated This function is ONLY for legacy migration from Flux/Baobab stores. */
export function legacyCreateReferencePanelStore() {
  return createStoreInstance<ReferencePanelState, ReferencePanelActions>(initialState, referencePanelActionsFactory, {
    storeName: STORE_NAME,
  });
}

export const usePersistReferencePanelStore = () => {
  const register = useRegisterPersistedStore();
  const store = useReferencePanelStoreInstance();

  useEffect(
    () =>
      register({
        storeName: 'sqReferencePanelStore',
        persistenceLevel: 'WORKSHEET',
        store,
        dehydrate: dehydrateReferencePanelState,
      }),
    [register, store],
  );
};
